import React, { useEffect, useRef, useState } from 'react'

import PolygonIcon from 'components/icons/PolygonIcon'

interface MenuOption {
  label: string
  key: string
  isChecked?: boolean
  subMenu?: MenuOption[]
  color?: string
}

export default function EditMenu({
  onSelect,
  options,
  position,
  onClose
}: {
  options: MenuOption[]
  position: { top: number; left: number }
  onSelect: (key: string) => void
  onClose: () => void
}) {
  const menuRef = useRef<HTMLDivElement>(null)
  const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  const renderMenuItem = (opt: MenuOption) => {
    return (
      <div
        key={opt.key}
        onClick={() => !opt.subMenu && onSelect(opt.key)}
        className="px-4 py-2 hover:bg-gray-100 cursor-pointer gap-x-4 flex items-center justify-between relative z-1"
        onMouseEnter={() =>
          opt.subMenu ? setActiveSubMenu(opt.key) : setActiveSubMenu(null)
        }
      >
        {opt.subMenu && (
          <div className="flex items-center">
            <PolygonIcon />
          </div>
        )}
        <span
          className="flex-1 text-xs"
          style={{
            color: opt.color
          }}
        >
          {opt.label}
        </span>

        {/* SubMenu rendering */}
        {opt.subMenu && activeSubMenu === opt.key && (
          <div
            className="absolute bg-white text-black shadow-2xl !rounded-lg py-2 w-48 border border-gray-100"
            style={{
              right: '100%',
              top: '-5px',
              marginRight: '1px',
              zIndex: 1
            }}
            onMouseEnter={() => setActiveSubMenu(opt.key)}
            onMouseLeave={() => setActiveSubMenu(null)}
          >
            {opt.subMenu.map((subOpt, subIndex) => (
              <React.Fragment key={subOpt.key}>
                <div
                  onClick={() => onSelect(subOpt.key)}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-xs"
                >
                  {subOpt.label}
                </div>
                {Number(subIndex) !== opt.subMenu!.length - 1 && (
                  <hr className="my-2 border-[0.01px] border-[#e4e4e450]" />
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white text-black shadow-2xl !rounded-lg py-2 w-48 border border-gray-100"
      style={{ top: position.top, left: position.left }}
      onMouseLeave={onClose}
    >
      {options.map((opt, index) => (
        <div key={opt.key}>
          {renderMenuItem(opt)}
          {Number(index) !== options.length - 1 && (
            <hr className="my-2 border-[0.01px] border-[#e4e4e450]" />
          )}
        </div>
      ))}
    </div>
  )
}
